/** PrivateChat (P5.4 / EVAS-14) — the manager's private side-channel to the agent.
 *
 * Why a separate panel instead of a "private" toggle inside the prebuilt `<Chat>`: `<Chat>` owns the
 * `lk.chat` topic and renders whatever arrives on it, with no per-message routing or private styling.
 * Mixing the two in one transcript is also a live-call footgun — a manager mis-reading which mode they
 * are in would say something private out loud to the visitor. Two visually distinct panels make the
 * boundary unmistakable, and the private one publishes on its OWN topic addressed to the agent alone
 * (`destinationIdentities`), so the visitor's client never receives the packets.
 *
 * The agent answers in TEXT on the same topic (non-voice LLM path — see agent-core/private-chat.ts).
 *
 * EVAS-117: the agent also pushes ONE unsolicited line here — the join briefing, marked
 * `kind: "briefing"` on the wire. It arrives before the manager has touched anything, so the panel
 * cannot merely append it: `onBriefing` asks the parent to OPEN this panel, or the briefing would sit
 * unread behind a closed toggle and the feature would do nothing. */
import {
  PRIVATE_CHAT_AGENT_LEFT_NOTICE,
  PRIVATE_CHAT_TOPIC,
  PRIVATE_CHAT_UNREACHABLE_NOTICE,
} from "@evas/shared";
import { useRemoteParticipants, useRoomContext } from "@livekit/components-react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  appendPrivateLine,
  buildPrivateQuestion,
  findAgentIdentity,
  type PrivateLine,
  parseAgentReply,
} from "./private-chat.js";

/** Minimal shape of a livekit-client text reader (the concrete type isn't exported from the root). */
interface TextReaderLike {
  readAll(): Promise<string>;
}

export function PrivateChat({
  visible,
  onBriefing,
}: {
  visible: boolean;
  /** Called when the agent pushes an unsolicited join briefing — the parent reveals the panel. */
  onBriefing?: () => void;
}) {
  const room = useRoomContext();
  const [lines, setLines] = useState<readonly PrivateLine[]>([]);
  const [draft, setDraft] = useState("");
  // How many of the manager's questions are unanswered (EVAS-398). A count, not a flag: the composer
  // stays open while the agent thinks, so the first answer must not clear a second question's placeholder.
  // Answers are not matched to questions; each one settles the oldest.
  const [pending, setPending] = useState(0);
  const seq = useRef(0);
  const endRef = useRef<HTMLLIElement>(null);
  // Read through a ref rather than an effect dependency: the topic handler must be registered ONCE for
  // the panel's lifetime, and an inline `onBriefing={() => …}` from the parent changes identity every
  // render — which would unregister and re-register on each one, with a window where a line arriving
  // in between is lost.
  const onBriefingRef = useRef(onBriefing);
  // Kept current in an effect rather than assigned during render (no render-phase writes), and declared
  // before the registration effect below so it has already run by the time a line can arrive. The
  // initial value comes from `useRef`, so the ref is correct from the first mount either way.
  useEffect(() => {
    onBriefingRef.current = onBriefing;
  }, [onBriefing]);

  const add = useCallback((from: "manager" | "agent", text: string, briefing = false) => {
    seq.current += 1;
    setLines((prev) =>
      appendPrivateLine(prev, {
        id: `p${seq.current}`,
        from,
        text,
        ...(briefing ? { briefing } : {}),
      }),
    );
  }, []);

  // Receive the agent's private lines. Registered for the lifetime of the panel (not gated on
  // `visible`) so an answer — or the join briefing — that lands while the panel is closed isn't lost.
  useEffect(() => {
    const handler = (reader: TextReaderLike): void => {
      void (async () => {
        try {
          const line = parseAgentReply(await reader.readAll());
          if (!line) return;
          add("agent", line.text, line.briefing);
          // Only an ANSWER clears the "thinking…" placeholder: a briefing is not a reply to anything,
          // so it must not cancel a question the manager asked in the same breath.
          if (line.briefing) onBriefingRef.current?.();
          else setPending((n) => Math.max(0, n - 1));
        } catch {
          // A failed read only costs one private line; never break the room.
        }
      })();
    };
    try {
      room.registerTextStreamHandler(PRIVATE_CHAT_TOPIC, handler);
    } catch {
      return; // already registered (e.g. StrictMode double-mount) — the first handler stands
    }
    return () => {
      try {
        room.unregisterTextStreamHandler(PRIVATE_CHAT_TOPIC);
      } catch {
        // best-effort cleanup
      }
    };
  }, [room, add]);

  // Keep the newest line in view. The newest line's id is the trigger, not the count: the thread is capped
  // (MAX_PRIVATE_LINES), so once full its length stops changing while every new line still arrives (review
  // of #444).
  const lastLineId = lines.at(-1)?.id;
  useEffect(() => {
    if (visible && lastLineId) endRef.current?.scrollIntoView({ block: "end" });
  }, [lastLineId, visible]);

  // From the hook, not a read of the room's map: the panel must re-render when the agent joins or leaves.
  const remotes = useRemoteParticipants();
  const agentIdentity = findAgentIdentity(remotes.map((p) => p.identity));

  // EVAS-398: a question is answered by the job that received it. When that agent leaves the call, or is
  // replaced by a new job (a new identity, after a restart), the open questions can no longer be answered:
  // end their placeholders and say so, rather than "thinking…" for ever.
  const lastAgent = useRef(agentIdentity);
  useEffect(() => {
    const before = lastAgent.current;
    lastAgent.current = agentIdentity;
    if (!before || before === agentIdentity || pending === 0) return;
    setPending(0);
    add("agent", PRIVATE_CHAT_AGENT_LEFT_NOTICE);
  }, [agentIdentity, pending, add]);

  const send = useCallback(() => {
    const frame = buildPrivateQuestion(draft);
    if (!frame || !agentIdentity) return;
    setDraft("");
    add("manager", frame.text);
    setPending((n) => n + 1);
    void room.localParticipant
      .sendText(JSON.stringify(frame), {
        topic: PRIVATE_CHAT_TOPIC,
        // The privacy guarantee: addressed to the agent only, so the visitor's client never sees it.
        destinationIdentities: [agentIdentity],
      })
      .catch((err) => {
        console.error("private question failed", err);
        setPending((n) => Math.max(0, n - 1));
        add("agent", PRIVATE_CHAT_UNREACHABLE_NOTICE);
      });
  }, [draft, agentIdentity, room, add]);

  return (
    <div className="manager-private-chat" style={{ display: visible ? "grid" : "none" }}>
      <div className="manager-private-header">
        <span className="manager-private-badge">Private</span>
        Only you and the assistant — the visitor sees and hears nothing here.
      </div>
      <ul className="manager-private-lines lk-list">
        {lines.length === 0 && (
          <li className="manager-private-empty">
            Ask the assistant anything about this call — what the visitor said, a summary, or a
            harder question. It answers here in text only, so you can use it while you talk.
          </li>
        )}
        {lines.map((l) => (
          <li
            key={l.id}
            className="manager-private-line"
            data-lk-from={l.from}
            {...(l.briefing ? { "data-lk-kind": "briefing" } : {})}
          >
            <span className="manager-private-from">
              {l.briefing ? "Briefing" : l.from === "manager" ? "You" : "Assistant"}
            </span>
            <span className="manager-private-text">{l.text}</span>
          </li>
        ))}
        {pending > 0 && (
          <li className="manager-private-line" data-lk-from="agent">
            <span className="manager-private-from">Assistant</span>
            <span className="manager-private-text manager-private-pending">thinking…</span>
          </li>
        )}
        {/* The scroll anchor: an <li>, since a <ul> may hold only list items (review of #444). */}
        <li ref={endRef} aria-hidden="true" />
      </ul>
      <form
        className="manager-private-form"
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
      >
        <input
          className="lk-form-control manager-private-input"
          placeholder={agentIdentity ? "Ask the assistant privately…" : "Assistant not in the call"}
          value={draft}
          disabled={!agentIdentity}
          onChange={(e) => setDraft(e.target.value)}
        />
        <button type="submit" className="lk-button" disabled={!agentIdentity || !draft.trim()}>
          Ask
        </button>
      </form>
    </div>
  );
}
